import { theme, useResponsive } from "@product/scmp-sdk";
import type { useSyncedRef } from "@react-hookz/web";
import { type Dispatch, type SetStateAction, useCallback } from "react";
import type { YouTubePlayer } from "youtube-player/dist/types";

import { AutoplayMode } from "~/components/scmp-youtube-video/scmp-youtube-video-player/enums";

type UseIntersectionHandlerParameters = {
  adsManager: OrUndefined<google.ima.AdsManager>;
  latestIsAdPlayingReference: ReturnType<typeof useSyncedRef<boolean>>;
  latestIsAdStartedReference: ReturnType<typeof useSyncedRef<OrUndefined<boolean>>>;
  player: OrUndefined<YouTubePlayer>;
  setAutoplayMode: Dispatch<SetStateAction<AutoplayMode>>;
};

export const useIntersectionHandler = ({
  adsManager,
  latestIsAdPlayingReference,
  latestIsAdStartedReference,
  player,
  setAutoplayMode,
}: UseIntersectionHandlerParameters) => {
  const isDesktopUp = useResponsive(theme.breakpoints.up("desktop"));

  const handleIsIntersecting = useCallback(() => {
    if (latestIsAdStartedReference.current) {
      adsManager?.resume();
    }

    // Only desktop up will play video
    if (!latestIsAdStartedReference.current && isDesktopUp) {
      try {
        void player?.playVideo();
      } catch (error) {
        console.error("handleIsIntersecting playVideo", error);
      }
    }
    setAutoplayMode(AutoplayMode.Playing);
  }, [adsManager, isDesktopUp, latestIsAdStartedReference, player, setAutoplayMode]);

  const handleIsNotIntersecting = useCallback(() => {
    if (!latestIsAdStartedReference.current && latestIsAdPlayingReference.current) {
      adsManager?.pause();
    }
    try {
      void player?.pauseVideo();
    } catch (error) {
      console.error("handleIsNotIntersecting pauseVideo", error);
    }
    setAutoplayMode(AutoplayMode.Pausing);
  }, [adsManager, latestIsAdPlayingReference, latestIsAdStartedReference, player, setAutoplayMode]);

  return {
    handleIsIntersecting,
    handleIsNotIntersecting,
  };
};
